import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { MdArrowBack, MdSearch } from 'react-icons/md'
import helpService from '../../services/helpService'
import HelpText from './HelpText'
import styles from './Help.module.scss'

const PORTAL_PLATFORMS = ['ios', 'android', 'desktop', 'web', 'portal']
const MIN_QUERY_LENGTH = 2
const SEARCH_DELAY_MS = 300

// Runs a request and keeps the latest result only
const useRequest = (load, deps, skip) => {
  const [state, setState] = useState({ loading: !skip, error: false, articles: null })
  useEffect(() => {
    if (skip) return undefined
    let ignore = false
    setState((prev) => ({ ...prev, loading: true, error: false }))
    load()
      .then(
        (res) =>
          !ignore && setState({ loading: false, error: false, articles: res?.articles || [] }),
      )
      .catch(() => !ignore && setState({ loading: false, error: true, articles: null }))
    return () => {
      ignore = true
    }
  }, [...deps, skip])
  return state
}

const Article = ({ slug, onBack }) => {
  const [state, setState] = useState({ loading: true, error: false, article: null })
  useEffect(() => {
    let ignore = false
    setState({ loading: true, error: false, article: null })
    helpService
      .getArticle(slug)
      .then((res) => !ignore && setState({ loading: false, error: false, article: res?.article }))
      .catch(() => !ignore && setState({ loading: false, error: true, article: null }))
    return () => {
      ignore = true
    }
  }, [slug])

  return (
    <div>
      <button type='button' className={styles.back} onClick={onBack}>
        <MdArrowBack /> Back
      </button>
      {state.loading ? <p className={styles.note}>Loading...</p> : null}
      {state.error ? <p className={styles.note}>This article is not available.</p> : null}
      {state.article ? (
        <article className={styles.article}>
          <h2>{state.article.title}</h2>
          <HelpText text={state.article.body} />
        </article>
      ) : null}
    </div>
  )
}

Article.propTypes = { slug: PropTypes.string, onBack: PropTypes.func }

/**
 * Help with search, a list of topics and the text of a topic.
 * context: screen key, shows the topics for that screen first (a link shows all of them)
 */
const HelpPanel = ({ platform, context, slug, onSlugChange }) => {
  const helpPlatform = PORTAL_PLATFORMS.includes(platform) ? platform : 'portal'
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [showAll, setShowAll] = useState(!context)
  const [openSlug, setOpenSlug] = useState(slug || null)
  const searching = debounced.length >= MIN_QUERY_LENGTH

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => setOpenSlug(slug || null), [slug])

  const list = useRequest(
    () => helpService.getArticles(helpPlatform, showAll ? undefined : context),
    [helpPlatform, context, showAll],
    false,
  )
  const search = useRequest(
    () => helpService.searchArticles(debounced, helpPlatform),
    [debounced, helpPlatform],
    !searching,
  )

  // A screen without its own topics falls back to all topics
  useEffect(() => {
    if (!showAll && list.articles && !list.articles.length) setShowAll(true)
  }, [showAll, list.articles])

  const openArticle = (value) => {
    setOpenSlug(value)
    if (onSlugChange) onSlugChange(value)
  }

  if (openSlug) return <Article slug={openSlug} onBack={() => openArticle(null)} />

  const shown = searching ? search : list

  return (
    <div className={styles.panel}>
      <div className={styles.searchWrapper}>
        <MdSearch className={styles.searchIcon} />
        <input
          type='search'
          className={styles.searchInput}
          placeholder='Search help'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>
      {shown.error ? <p className={styles.note}>Help is not available right now.</p> : null}
      {!shown.error && shown.loading ? <p className={styles.note}>Loading...</p> : null}
      {!shown.error && !shown.loading && shown.articles && !shown.articles.length ? (
        <p className={styles.note}>
          {searching ? 'Nothing found. Try a different word.' : 'No topics yet.'}
        </p>
      ) : null}
      {!shown.error && !shown.loading && shown.articles?.length ? (
        <ul className={styles.list}>
          {shown.articles.map((article) => (
            <li key={article.slug}>
              <button
                type='button'
                className={styles.item}
                onClick={() => openArticle(article.slug)}
              >
                <span className={styles.itemTitle}>{article.title}</span>
                {article.summary ? (
                  <span className={styles.itemSummary}>{article.summary}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!searching && !showAll ? (
        <button type='button' className={styles.showAll} onClick={() => setShowAll(true)}>
          Show all topics
        </button>
      ) : null}
    </div>
  )
}

HelpPanel.propTypes = {
  platform: PropTypes.string,
  context: PropTypes.string,
  slug: PropTypes.string,
  onSlugChange: PropTypes.func,
}

export default HelpPanel
